import { useContext, useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { selectAutoCompleteSuggestions } from "Store/Selectors/UI";
import { endsWithWhitespace, isNullOrWhiteSpace } from "Utils/String";
import { getLastSearchTerm } from "Utils/Tokenizr";
import { SearchContext } from "../../SearchContext";

export function useSuggestionList(): string[] {
  const {
    searchTerm, setSearchHighlight, setIsCallOutVisible, isCallOutVisible
  } = useContext(SearchContext);
  const suggestionList = useSelector(selectAutoCompleteSuggestions);
  const [filteredSuggestionList, setFilteredSuggestionList] = useState(suggestionList);

  useEffect(() => {
    const filterExpression = getLastSearchTerm(searchTerm);
    setFilteredSuggestionList((list) => list.reduce((output, fieldName) => {
      if (fieldName === filterExpression?.fieldName) {
        return output;
      }

      output.push(fieldName);
      return output;
    }, []));
  }, [isCallOutVisible, searchTerm]);

  useEffect(() => {
    const isEmptySearchTerm = isNullOrWhiteSpace(searchTerm);
    const isFiltered = filteredSuggestionList?.length !== suggestionList?.length;

    if (isEmptySearchTerm && isFiltered) {
      // reset filtered list
      setFilteredSuggestionList(suggestionList);
      setSearchHighlight(null);
      return;
    }

    const { fieldName: checkForStar, searchTerm: potentialFieldName } = getLastSearchTerm(searchTerm);
    const isNewSearch = checkForStar === `*`;

    if (!isNewSearch && !isOpenQuoteSearch(searchTerm) && !endsWithWhitespace(searchTerm)) {
      // we're currently not looking for field names
      setFilteredSuggestionList(suggestionList);
      setIsCallOutVisible(false);
      setSearchHighlight(null);
      return;
    }

    let normalizedFieldName = null;
    if (isNewSearch) {
      normalizedFieldName = potentialFieldName?.toLowerCase();
      setSearchHighlight(normalizedFieldName);
    }

    const lastExpression = getLastSearchTerm(searchTerm);
    const isWhitespaceSearch = endsWithWhitespace(searchTerm);

    const filteredList = suggestionList.filter((suggestion) => {
      const fieldName = suggestion.toLowerCase();

      if (fieldName === lastExpression?.fieldName) {
        return false;
      }

      if (isWhitespaceSearch) {
        return true;
      }

      return fieldName.includes(normalizedFieldName);
    });

    setFilteredSuggestionList(filteredList);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, suggestionList]);

  return filteredSuggestionList;
}

function isOpenQuoteSearch(search: string): boolean {
  if (!/['"]/i.test(search)) {
    // does not contain quotes at all
    return false;
  }

  // test if latest quote is closed
  // test: https://regex101.com/r/dsiBmL/1
  if (/["'][^'"]*?["']\s*$/i.test(search)) {
    return false;
  }

  return true;
}
